import { BonificoOrderStatus } from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import {
  BONIFICO_ORDERS_PAGE_SIZE,
  BonificoOrderSortField,
  BonificoOrdersFilters,
  BonificoOrdersListQuery,
  DEFAULT_BONIFICO_ORDERS_SORT,
  SortDirection,
} from "../../ProductService/BonificoOrder/BonificoOrdersListQuery.ts";
import {
  centsToEuros,
  parseCalendarDate,
  parseEurosToCents,
  parseKnownValues,
  parseList,
  parsePageIndex,
  parseSort,
  setOrDelete,
  withListPage,
  withListSearch,
  withListSort,
} from "../../UI/Table/listUrlParams.ts";

const SORT_FIELDS: BonificoOrderSortField[] = [
  "createdAt",
  "customer",
  "amount",
  "receiptDate",
  "valueDate",
];

const DATE_FILTER_PARAMS = [
  "createdFrom",
  "createdTo",
  "receiptFrom",
  "receiptTo",
  "valueFrom",
  "valueTo",
] as const;

const FLAG_FILTER_PARAMS = ["receiptMissing", "valueMissing"] as const;

const FILTER_PARAMS = [
  "status",
  "product",
  "amountMin",
  "amountMax",
  ...DATE_FILTER_PARAMS,
  ...FLAG_FILTER_PARAMS,
];

export function parseBonificoOrdersListQuery(
  params: URLSearchParams,
): BonificoOrdersListQuery {
  return {
    search: params.get("search") || undefined,
    statuses: parseKnownValues(
      params.get("status"),
      Object.values(BonificoOrderStatus),
    ),
    productIds: parseList(params.get("product")),
    amountMinInCents: parseEurosToCents(params.get("amountMin")),
    amountMaxInCents: parseEurosToCents(params.get("amountMax")),
    ...Object.fromEntries(
      DATE_FILTER_PARAMS.map((param) => [
        param,
        parseCalendarDate(params.get(param)),
      ]),
    ),
    ...Object.fromEntries(
      FLAG_FILTER_PARAMS.map((param) => [param, params.get(param) === "1"]),
    ),
    ...parseSort(params.get("sort"), SORT_FIELDS, DEFAULT_BONIFICO_ORDERS_SORT),
    page: parsePageIndex(params.get("page")),
    pageSize: BONIFICO_ORDERS_PAGE_SIZE,
  };
}

export function withBonificoOrdersFilters(
  current: URLSearchParams,
  filters: BonificoOrdersFilters,
): URLSearchParams {
  const params = new URLSearchParams(current);
  FILTER_PARAMS.forEach((param) => params.delete(param));
  setOrDelete(params, "status", filters.statuses?.join(","));
  setOrDelete(params, "product", filters.productIds?.join(","));
  setOrDelete(params, "amountMin", centsToEuros(filters.amountMinInCents));
  setOrDelete(params, "amountMax", centsToEuros(filters.amountMaxInCents));
  DATE_FILTER_PARAMS.forEach((param) =>
    setOrDelete(params, param, filters[param]),
  );
  FLAG_FILTER_PARAMS.forEach((param) =>
    setOrDelete(params, param, filters[param] ? "1" : undefined),
  );
  params.delete("page");
  return params;
}

export function withBonificoOrdersSort(
  current: URLSearchParams,
  sortBy: BonificoOrderSortField,
  sortDirection: SortDirection,
): URLSearchParams {
  return withListSort(
    current,
    { sortBy, sortDirection },
    DEFAULT_BONIFICO_ORDERS_SORT,
  );
}

export const withBonificoOrdersSearch = withListSearch;

export const withBonificoOrdersPage = withListPage;
